import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";

import type { ReservationOperationItem } from "@bw/shared/types/generated";
import { formatMoney } from "@bw/shared/utils";

interface DateTimeFormatters {
  date: Intl.DateTimeFormat;
  time: Intl.DateTimeFormat;
}

function createFormatters(timeZone: string): DateTimeFormatters {
  return {
    date: new Intl.DateTimeFormat("en-US", {
      timeZone,
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }),
  };
}

function statusLabel(status: string): string {
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function statusColor(
  status: string,
): "default" | "error" | "info" | "success" | "warning" {
  if (status === "confirmed") return "success";
  if (status === "completed") return "info";
  if (status === "cancelled") return "error";
  if (status === "no_show") return "warning";
  return "default";
}

export interface ReservationOperationsTableProps {
  items: ReservationOperationItem[];
  total: number;
  isLoading?: boolean;
  emptyMessage?: string;
  onSelect?: (item: ReservationOperationItem) => void;
}

export function ReservationOperationsTable({
  items,
  total,
  isLoading = false,
  emptyMessage = "No reservations match these filters.",
  onSelect,
}: ReservationOperationsTableProps) {
  const formatters = useMemo(() => {
    const result = new Map<string, DateTimeFormatters>();
    for (const item of items) {
      if (!result.has(item.facility_timezone)) {
        result.set(
          item.facility_timezone,
          createFormatters(item.facility_timezone),
        );
      }
    }
    return result;
  }, [items]);

  return (
    <TableContainer
      sx={{
        width: "100%",
        overflowX: "auto",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Table
        size="small"
        aria-label={
          isLoading
            ? "Reservations loading"
            : `Reservations, showing ${items.length} of ${total}`
        }
        aria-busy={isLoading}
        sx={{
          tableLayout: "fixed",
          width: "100%",
          minWidth: { xs: 0, lg: 820 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                width: { xs: "36%", sm: "32%", lg: "26%" },
                px: { xs: 1, sm: 2 },
                overflowWrap: "anywhere",
              }}
            >
              Player / event
            </TableCell>
            <TableCell
              sx={{
                width: { xs: "38%", sm: "32%", lg: "24%" },
                px: { xs: 1, sm: 2 },
                overflowWrap: "anywhere",
              }}
            >
              Facility / court
            </TableCell>
            <TableCell
              sx={{
                width: { lg: "22%" },
                display: { xs: "none", lg: "table-cell" },
                overflowWrap: "anywhere",
              }}
            >
              Local time
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: { sm: "16%", lg: "14%" },
                display: { xs: "none", sm: "table-cell" },
                overflowWrap: "anywhere",
              }}
            >
              Amount
            </TableCell>
            <TableCell
              sx={{
                width: { xs: "26%", sm: "20%", lg: "14%" },
                px: { xs: 1, sm: 2 },
                overflowWrap: "anywhere",
              }}
            >
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ height: 80 }}>
                <Stack
                  role="status"
                  aria-live="polite"
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center", justifyContent: "center" }}
                >
                  <CircularProgress size={18} />
                  <Typography color="text.secondary" variant="body2">
                    Loading reservations...
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ height: 96 }}>
                <Typography
                  role="status"
                  aria-live="polite"
                  color="text.secondary"
                  variant="body2"
                >
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => {
              const itemFormatters =
                formatters.get(item.facility_timezone) ??
                createFormatters("UTC");
              const isBlock = Boolean(item.event_name);
              const primaryLabel = isBlock
                ? item.event_name?.trim() || "Court block"
                : item.display_name?.trim() || "Reserved";
              const date = itemFormatters.date.format(
                new Date(item.start_time),
              );
              const time = `${itemFormatters.time.format(
                new Date(item.start_time),
              )} – ${itemFormatters.time.format(new Date(item.end_time))}`;
              const amount = isBlock
                ? "No charge"
                : formatMoney(item.amount_charged, item.currency);

              return (
                <TableRow
                  key={item.booking_id}
                  hover={Boolean(onSelect)}
                  onClick={() => onSelect?.(item)}
                  sx={{ cursor: onSelect ? "pointer" : "default" }}
                >
                  <TableCell
                    sx={{
                      width: { xs: "36%", sm: "32%", lg: "26%" },
                      px: { xs: 1, sm: 2 },
                      verticalAlign: "top",
                      overflowWrap: "anywhere",
                    }}
                  >
                    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center", minWidth: 0 }}
                      >
                        {onSelect ? (
                          <ButtonBase
                            aria-label={`Open reservation for ${primaryLabel}, ${item.facility_name}, ${date}, ${time}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              onSelect(item);
                            }}
                            sx={{
                              minWidth: 0,
                              maxWidth: "100%",
                              borderRadius: 0.5,
                              justifyContent: "flex-start",
                              textAlign: "left",
                              "&:focus-visible": {
                                outline: "2px solid",
                                outlineColor: "primary.main",
                                outlineOffset: 2,
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
                            >
                              {primaryLabel}
                            </Typography>
                          </ButtonBase>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
                          >
                            {primaryLabel}
                          </Typography>
                        )}
                        {isBlock ? (
                          <Chip
                            data-testid="block-badge"
                            icon={<EventBusyOutlinedIcon aria-hidden="true" />}
                            label="Block"
                            size="small"
                            variant="outlined"
                            sx={{
                              maxWidth: "100%",
                              height: "auto",
                              "& .MuiChip-label": {
                                py: 0.5,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                              },
                            }}
                          />
                        ) : null}
                      </Stack>
                      <Box sx={{ display: { xs: "block", lg: "none" } }}>
                        <Typography color="text.secondary" variant="caption">
                          {date}
                        </Typography>
                        <Typography
                          component="div"
                          color="text.secondary"
                          variant="caption"
                        >
                          {time}
                        </Typography>
                        <Typography
                          component="div"
                          variant="caption"
                          sx={{ display: { xs: "block", sm: "none" } }}
                        >
                          {amount}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: { xs: "38%", sm: "32%", lg: "24%" },
                      px: { xs: 1, sm: 2 },
                      verticalAlign: "top",
                      overflowWrap: "anywhere",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
                    >
                      {item.facility_name}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {item.court_name}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: { lg: "22%" },
                      display: { xs: "none", lg: "table-cell" },
                      overflowWrap: "anywhere",
                    }}
                  >
                    <Typography variant="body2">{date}</Typography>
                    <Typography color="text.secondary" variant="caption">
                      {time}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: { sm: "16%", lg: "14%" },
                      display: { xs: "none", sm: "table-cell" },
                      overflowWrap: "anywhere",
                    }}
                  >
                    {amount}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: { xs: "26%", sm: "20%", lg: "14%" },
                      px: { xs: 1, sm: 2 },
                      overflowWrap: "anywhere",
                    }}
                  >
                    <Chip
                      label={
                        item.cancellation_recovery_pending
                          ? "Cancellation processing"
                          : statusLabel(item.status)
                      }
                      color={
                        item.cancellation_recovery_pending
                          ? "warning"
                          : statusColor(item.status)
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        maxWidth: "100%",
                        height: "auto",
                        "& .MuiChip-label": {
                          py: 0.5,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
